/**
 * Live cards, driver side (Warren, 28 Oct 2026): what a `live` event on
 * /me/session-events makes stale.
 *
 * The Api sends { kind, id, location_id, charger_id } for the driver's own
 * sessions, payouts and points, and for availability changes at the
 * chargers the driver could see (owner, pass list, open to guests, own
 * tenant; never a private charger the driver has no pass for). Ids only:
 * sessionEventStream batches them, at most one round a second, and
 * invalidates the queries this table assigns to the kinds that arrived; the
 * ones on screen refetch through their ordinary routes.
 *
 * LIVE_QUERY_KEYS is the one place that says which screen reads which
 * kind. Patterns are key prefixes, ANY matching one element.
 *
 * Run the tests: `tsx --test src/lib/liveInvalidation.test.ts` from apps/web/.
 */

export const LIVE_KINDS = ['session', 'charger', 'payout', 'points'] as const;
export type LiveKind = typeof LIVE_KINDS[number];

export interface LiveEvent {
  kind: LiveKind;
  id: string;
  location_id: string | null;
  charger_id: string | null;
}

export const ANY = '*';
export type KeyPattern = readonly string[];

/**
 * Event kind to the query-key prefixes it makes stale: the driver's stats,
 * history, sessions, map, location and charger views. Not here, on purpose:
 * notifications, the friends leaderboard (a cron snapshot), Stripe-backed
 * screens (payment methods, Connect) and charger alerts. No event covers
 * their data, so they keep their own refresh.
 */
export const LIVE_QUERY_KEYS: Readonly<Record<LiveKind, readonly KeyPattern[]>> = {
  session: [
    // The live session and its detail, history and receipts.
    ['sessions'],
    ['session'],
    ['recent-sessions'],
    ['session-hold'],
    ['me', 'sessions'],
    ['me', 'activity'],
    // Stats cards: charging summary, carbon, rewards, streaks, fleet.
    ['me', 'charging'],
    ['me', 'carbon-summary'],
    ['gamification'],
    ['streaks'],
    ['fleet-stats'],
    ['my-home-sessions'],
    ['wallet'],
  ],
  charger: [
    // Map, location and charger views.
    ['public-locations'],
    ['public-location'],
    ['public-charger'],
    ['start-preview'],
    // The driver's own chargers and home.
    ['my-chargers'],
    ['myChargers'],
    ['my-charger'],
    ['my-home'],
    ['my-home-sessions'],
  ],
  payout: [
    ['session'],
    ['sessions'],
    ['me', 'sessions'],
    ['outstanding'],
    ['wallet'],
  ],
  points: [
    ['gamification'],
    ['streaks'],
    ['challenges'],
    ['badges'],
    ['me', 'activity'],
    ['session', ANY, 'points'],
  ],
};

/** A key matches a pattern when it starts with it, ANY matching any element. */
export function keyMatches(pattern: KeyPattern, key: readonly unknown[]): boolean {
  if (key.length < pattern.length) return false;
  return pattern.every((p, i) => p === ANY || p === key[i]);
}

/** Whether a query key belongs to any of these kinds. */
export function keyIsLiveFor(key: readonly unknown[], kinds: Iterable<LiveKind>): boolean {
  for (const kind of kinds) {
    if (LIVE_QUERY_KEYS[kind].some((p) => keyMatches(p, key))) return true;
  }
  return false;
}

/** Parse one `live` event's data; null for anything malformed. */
export function parseLiveEvent(data: string): LiveEvent | null {
  try {
    const v = JSON.parse(data) as Partial<LiveEvent> | null;
    if (!v || typeof v !== 'object') return null;
    if (!LIVE_KINDS.includes(v.kind as LiveKind) || typeof v.id !== 'string') return null;
    const str = (x: unknown) => (typeof x === 'string' ? x : null);
    return { kind: v.kind as LiveKind, id: v.id, location_id: str(v.location_id), charger_id: str(v.charger_id) };
  } catch {
    return null;
  }
}

export const ALL_KINDS: ReadonlySet<LiveKind> = new Set(LIVE_KINDS);

type TimerHandle = unknown;

/**
 * At most one flush per interval: the first kind after a quiet spell
 * flushes at once, later ones inside the interval wait for one trailing
 * flush. `catchUp` marks every kind (a reconnect after a gap).
 */
export function createLiveBatcher(opts: {
  intervalMs: number;
  onFlush: (kinds: ReadonlySet<LiveKind>) => void;
  now?: () => number;
  setTimer?: (fn: () => void, ms: number) => TimerHandle;
  clearTimer?: (h: TimerHandle) => void;
}): { add: (kind: LiveKind) => void; catchUp: () => void; dispose: () => void } {
  const now = opts.now ?? (() => Date.now());
  const setTimer = opts.setTimer ?? ((fn, ms) => setTimeout(fn, ms));
  const clearTimer = opts.clearTimer ?? ((h) => clearTimeout(h as ReturnType<typeof setTimeout>));
  let pending = new Set<LiveKind>();
  let timer: TimerHandle | null = null;
  let lastFlush = Number.NEGATIVE_INFINITY;

  const flush = (): void => {
    timer = null;
    lastFlush = now();
    const kinds = pending;
    pending = new Set();
    if (kinds.size > 0) opts.onFlush(kinds);
  };
  const schedule = (): void => {
    if (timer !== null) return;
    const wait = Math.max(0, lastFlush + opts.intervalMs - now());
    if (wait === 0) flush();
    else timer = setTimer(flush, wait);
  };

  return {
    add(kind) {
      pending.add(kind);
      schedule();
    },
    catchUp() {
      for (const k of ALL_KINDS) pending.add(k);
      schedule();
    },
    dispose() {
      if (timer !== null) clearTimer(timer);
      timer = null;
      pending = new Set();
    },
  };
}

/**
 * The Api's live bus health on an open stream (Codex review finding 8,
 * 29 Oct 2026). `degraded`: one of its change streams was lost and live
 * events may be missing, so live screens poll again (live false, see
 * streamAwareInterval below). `recovered`: every stream is confirmed open
 * again, so polling stops and everything live is refetched once, because
 * what changed in the gap was never sent.
 */
export function applyLiveHealth(
  event: 'degraded' | 'recovered',
  to: { setLive: (live: boolean) => void; catchUp: () => void },
): void {
  if (event === 'recovered') {
    to.setLive(true);
    to.catchUp();
  } else {
    to.setLive(false);
  }
}

/** The Api's names for the two health events (apps/api/src/lib/liveBus.ts, liveHealthWire). */
export const LIVE_HEALTH_EVENTS = ['degraded', 'recovered'] as const;

/**
 * Wire the health events on one EventSource to applyLiveHealth.
 * sessionEventStream calls this for each connection it opens; `isCurrent`
 * says whether that connection is still the module's, so a replaced one
 * (a token refresh, a reconnect) cannot flip the store after the fact.
 */
export function listenLiveHealth(
  source: { addEventListener: (name: string, listener: () => void) => void },
  to: { isCurrent: () => boolean; setLive: (live: boolean) => void; catchUp: () => void },
): void {
  for (const name of LIVE_HEALTH_EVENTS) {
    source.addEventListener(name, () => {
      if (!to.isCurrent()) return;
      applyLiveHealth(name, to);
    });
  }
}

/**
 * The poll a live screen falls back to. With the stream open and the live
 * bus running, nothing polls (false); open without the bus, the old 60 s
 * heartbeat; otherwise the screen's own cadence.
 */
export function streamAwareInterval(
  status: 'idle' | 'connecting' | 'open' | 'error',
  live: boolean,
  fallbackMs: number,
): number | false {
  if (status !== 'open') return fallbackMs;
  return live ? false : 60_000;
}
